import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { getPublicSiteSettings, saveLanguageSpecificSettings, savePublicSiteSettings } from '../../api/publicPages';
import {
  appearanceDraftFromData,
  buildAppearanceWrites,
  type AppearanceDraft,
  type AppearanceWriteCaps,
} from './appearanceDraft';

export {
  APPEARANCE_CAPS_FREE,
  APPEARANCE_CAPS_STANDARD,
  BUTTON_POSITION_OPTIONS,
  DEFAULT_APPEARANCE_COLOR,
  WIDGET_POSITION_OPTIONS,
  appearanceDraftFromData,
  appearanceDraftsEqual,
  buildAppearanceWrites,
  defaultAppearanceDraft,
} from './appearanceDraft';
export type {
  AppearanceCloseButton,
  AppearanceDraft,
  AppearanceIcon,
  AppearanceOffset,
  AppearanceSiteColors,
  AppearanceTooltip,
  AppearanceWriteCaps,
  ButtonPreset,
  WidgetPreset,
} from './appearanceDraft';

export async function loadAppearanceDraft(workspaceId: string): Promise<AppearanceDraft> {
  const [site, widgetSnap] = await Promise.all([
    getPublicSiteSettings(workspaceId),
    getDoc(doc(db, `publicPages/${workspaceId}/settings`, 'widget')),
  ]);
  const widget = widgetSnap.exists() ? (widgetSnap.data() as Record<string, unknown>) : null;
  const languageCode = site?.defaultLanguage;
  if (!languageCode) return appearanceDraftFromData(widget, site);

  const languageSnap = await getDoc(
    doc(db, `publicPages/${workspaceId}/settings/site/languages/${languageCode}`),
  );
  if (!languageSnap.exists()) return appearanceDraftFromData(widget, site);
  const language = languageSnap.data() as {
    contentTitle?: string;
    contentSubtitle?: string;
    contentPlaceholder?: string;
  };
  return appearanceDraftFromData(widget, {
    ...site,
    contentTitle: typeof language.contentTitle === 'string' ? language.contentTitle : site?.contentTitle,
    contentSubtitle: typeof language.contentSubtitle === 'string' ? language.contentSubtitle : site?.contentSubtitle,
    contentPlaceholder: typeof language.contentPlaceholder === 'string'
      ? language.contentPlaceholder
      : site?.contentPlaceholder,
  });
}

export async function saveAppearanceDraft(
  workspaceId: string,
  draft: AppearanceDraft,
  caps: AppearanceWriteCaps,
): Promise<void> {
  const widgetRef = doc(db, `publicPages/${workspaceId}/settings`, 'widget');
  const widgetSnap = await getDoc(widgetRef);
  const prev = widgetSnap.exists() ? (widgetSnap.data() as Record<string, unknown>) ?? {} : {};
  const { widget, site } = buildAppearanceWrites(prev, draft, caps);
  const writes: Promise<unknown>[] = [
    setDoc(widgetRef, {
      ...widget,
      updatedAt: serverTimestamp(),
    }),
  ];
  if (site) {
    writes.push(savePublicSiteSettings(workspaceId, site));
    if (caps.contentText && site.contentTitle !== undefined) {
      const current = await getPublicSiteSettings(workspaceId);
      const languageCode = current?.defaultLanguage;
      if (languageCode) {
        writes.push(saveLanguageSpecificSettings(workspaceId, languageCode, {
          contentTitle: site.contentTitle,
          contentSubtitle: site.contentSubtitle,
          contentPlaceholder: site.contentPlaceholder,
        }));
      }
    }
  }
  await Promise.all(writes);
}
